import { MoreVert, Save } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useMutateDeleteCropMonitoringNote, useMutateUpdateCropMonitoringNote } from '/@/hooks/crop-monitoring-notes';
import { _ } from '/@/locale';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { getUserDisplayName, RegistryFooter } from './card-footer';
import { EditActions } from './edit-actions';
import { RegistryCard } from './registry-card';

const Note = ({ note }: { note: CropMonitoringNote }) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState(note.content ?? '');

    const updateNote = useMutateUpdateCropMonitoringNote();
    const deleteNote = useMutateDeleteCropMonitoringNote();

    const authorName = getUserDisplayName(note.createdBy);

    const registeredAt = formatDate(new Date(note.created));

    const closeMenu = () => setMenuAnchor(null);

    const startEditing = () => {
        setContent(note.content ?? '');
        setIsEditing(true);
        closeMenu();
    };

    const cancelEditing = () => {
        setContent(note.content ?? '');
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (!content.trim()) return;
        await updateNote.trigger({ id: note.id, content: content.trim() });
        setIsEditing(false);
    };

    const handleDelete = async () => {
        closeMenu();
        await deleteNote.trigger({ id: note.id });
    };

    return (
        <RegistryCard>
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Typography variant='h6' color='primary'>
                    {_('Nota')}
                </Typography>
                <IconButton
                    size='small'
                    aria-label={_('Más opciones')}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                >
                    <MoreVert />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                    <MenuItem onClick={startEditing}>{_('Editar')}</MenuItem>
                    <MenuItem onClick={handleDelete} disabled={deleteNote.isMutating}>
                        {_('Eliminar')}
                    </MenuItem>
                </Menu>
            </Stack>

            {isEditing ? (
                <Stack spacing={1} width='100%'>
                    <TextField
                        fullWidth
                        multiline
                        value={content}
                        onChange={(event) => setContent(event.target.value)}
                        size='small'
                    />
                    <EditActions
                        onCancel={cancelEditing}
                        onSave={handleSave}
                        saveDisabled={!content.trim() || updateNote.isMutating}
                        saveEndIcon={<Save sx={{ fontSize: '12px !important' }} />}
                    />
                </Stack>
            ) : (
                note.content && (
                    <Typography variant='body2' color='primary'>
                        {note.content}
                    </Typography>
                )
            )}

            <RegistryFooter authorName={authorName} date={registeredAt} />
        </RegistryCard>
    );
};

export default Note;
